import { AppCheck, initializeAppCheck, ReCaptchaEnterpriseProvider } from '@firebase/app-check';
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  getAuth,
  indexedDBLocalPersistence,
  inMemoryPersistence,
  setPersistence,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();
const db = getFirestore(app);
const auth = getAuth(app);
const storage = getStorage(app);

export type AuthPersistenceMode = 'durable' | 'session' | 'memory' | 'server';

async function configureAuthPersistence(): Promise<AuthPersistenceMode> {
  if (typeof window === 'undefined') {
    return 'server';
  }

  try {
    await setPersistence(auth, indexedDBLocalPersistence);
    return 'durable';
  } catch {
    try {
      await setPersistence(auth, browserLocalPersistence);
      return 'durable';
    } catch {
      try {
        await setPersistence(auth, browserSessionPersistence);
        return 'session';
      } catch {
        try {
          await setPersistence(auth, inMemoryPersistence);
        } catch {
          // getAuth() already selected Firebase's best available fallback.
        }
        return 'memory';
      }
    }
  }
}

export const authPersistenceReady = configureAuthPersistence();

// Initialize App Check
let appCheck: AppCheck | undefined;
if (typeof window !== 'undefined') {
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string),
    isTokenAutoRefreshEnabled: true,
  });
}


// Type definitions for Firestore timestamp objects
interface FirestoreTimestamp {
  toDate(): Date;
}

interface FirestoreTimestampObject {
  seconds: number;
  nanoseconds?: number;
}

interface FirestoreTimestampLegacy {
  _seconds: number;
  _nanoseconds?: number;
}

/**
 * Type guard to check if object has toDate method
 */
function hasToDateMethod(obj: unknown): obj is FirestoreTimestamp {
  return typeof obj === 'object' && 
         obj !== null && 
         'toDate' in obj && 
         typeof (obj as Record<string, unknown>).toDate === 'function';
}

/**
 * Type guard to check if object has seconds property
 */
function hasSecondsProperty(obj: unknown): obj is FirestoreTimestampObject {
  return typeof obj === 'object' && 
         obj !== null && 
         'seconds' in obj && 
         typeof (obj as Record<string, unknown>).seconds === 'number';
}

/**
 * Type guard to check if object has _seconds property (legacy format)
 */
function hasLegacySecondsProperty(obj: unknown): obj is FirestoreTimestampLegacy {
  return typeof obj === 'object' && 
         obj !== null && 
         '_seconds' in obj && 
         typeof (obj as Record<string, unknown>)._seconds === 'number';
}

/**
 * Converts various date formats from Firestore to JavaScript Date objects
 */
export function convertFirestoreTimestamp(timestamp: unknown): Date {
  if (!timestamp) {
    return new Date();
  }
  
  // Handle different date formats from Firestore
  if (hasToDateMethod(timestamp)) {
    // Firestore timestamp with toDate method
    return timestamp.toDate();
  } else if (hasLegacySecondsProperty(timestamp)) {
    // Firestore timestamp object with _seconds property (legacy)
    return new Date(timestamp._seconds * 1000);
  } else if (hasSecondsProperty(timestamp)) {
    // Firestore timestamp object with seconds property
    return new Date(timestamp.seconds * 1000);
  } else if (typeof timestamp === 'string') {
    // ISO string
    return new Date(timestamp);
  } else if (timestamp instanceof Date) {
    // Already a Date object
    return timestamp;
  } else {
    console.warn('Unknown date format:', timestamp);
    return new Date();
  }
}

export { app, db, auth, storage, appCheck };
